import { useEffect, useLayoutEffect, useState } from 'react';
import { embedContext } from './osEntry';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'hsp_theme';

// Sheahan Studios brand is paper/light by default. Dark is an opt-in theme,
// offered only on admin/editor/shoot screens via the toggle. Public pages force
// light on mount, so a client never sees dark regardless of stored preference.
const DEFAULT_THEME: Theme = 'light';

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {}
  return null;
}

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme;
}

export function initTheme() {
  apply(embedContext.theme ?? readStored() ?? DEFAULT_THEME);
}

export function useTheme(): { theme: Theme; toggle: () => void; set: (t: Theme) => void } {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document === 'undefined') return DEFAULT_THEME;
    return (document.documentElement.dataset.theme as Theme) || embedContext.theme || readStored() || DEFAULT_THEME;
  });

  useEffect(() => {
    apply(theme);
    try {
      if (!embedContext.embedded) localStorage.setItem(STORAGE_KEY, theme);
    } catch {}
  }, [theme]);

  return {
    theme,
    toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')),
    set: setTheme,
  };
}

/**
 * Force a specific theme for a public page for as long as `theme` holds a
 * given value, restoring whatever was set before on unmount (or when `theme`
 * changes again) — so an admin's stored dark preference never leaks onto a
 * client-facing screen. `theme` may change across renders (e.g. once an
 * async fetch resolves what kind of bundle this is), unlike a one-shot
 * mount effect.
 *
 * useLayoutEffect, not useEffect: the attribute must land before the browser
 * paints, or a cold dark load (e.g. a shoot bundle deep link) flashes the
 * page's default light background for one frame before flipping dark.
 * Cleanup semantics are unchanged — same read-prev/restore-prev body, just
 * scheduled synchronously instead of after paint.
 */
export function useForceTheme(theme: Theme) {
  useLayoutEffect(() => {
    const prev = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = theme;
    return () => {
      if (prev) document.documentElement.dataset.theme = prev;
    };
  }, [theme]);
}

/**
 * Force the paper/light theme for a public page and keep it forced for the
 * page's lifetime. Call from public pages (Landing, ClientReview,
 * ClientInterviewView) that never switch theme mid-life.
 */
export function useForceLight() {
  useForceTheme('light');
}
